import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { examApi, type ExamContent, type Question, type ReportQuestion } from '../../services/examApi';
import { QuestionReview } from './QuestionReview';
import { AITutorModal } from './AITutorModal';
import { DraggableResizablePanel } from '../common/DraggableResizablePanel';

interface Props {
  examId: string;
  target: { subject: string; moduleNo: number; sort: number };
  answers: Array<{ subject: string; moduleNo: number; sort: number; value: any }>;
  questions: ReportQuestion[];
  onClose: () => void;
}

// 模块级缓存：同一 examId 的内容只拉一次
const contentCache = new Map<string, ExamContent>();

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;

interface Rect { x: number; y: number; w: number; h: number; }

export const QuestionDetailModal: React.FC<Props> = ({ examId, target, answers, questions, onClose }) => {
  const [content, setContent] = useState<ExamContent | null>(contentCache.get(examId) ?? null);
  const [loading, setLoading] = useState(!contentCache.has(examId));
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  const [selecting, setSelecting] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const [shotMsg, setShotMsg] = useState<string | null>(null);
  const [shotDataUrl, setShotDataUrl] = useState<string | null>(null); // 截图 PNG dataURL
  const [aiOpen, setAiOpen] = useState(false);                          // AI 辅导窗
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);   // 滚动容器（截图基准 + overlay 定位基准）
  const contentRef = useRef<HTMLDivElement>(null);  // 被缩放的内容容器（html2canvas 目标）

  useEffect(() => {
    if (contentCache.has(examId)) return;
    let mounted = true;
    examApi
      .getContent(examId)
      .then((res) => { contentCache.set(examId, res); if (mounted) { setContent(res); setLoading(false); } })
      .catch((e: any) => { if (mounted) { setError(e?.message || 'Failed to load'); setLoading(false); } });
    return () => { mounted = false; };
  }, [examId]);

  const question: Question | null = useMemo(() => {
    if (!content) return null;
    const sec = content.content?.sections?.find((s) => s.subject === target.subject);
    const mod = sec?.modules?.find((m) => m.moduleNo === target.moduleNo);
    return mod?.questions?.find((q) => q.sort === target.sort) ?? null;
  }, [content, target]);

  const userAnswer = useMemo(() => {
    const a = answers.find((x) => x.subject === target.subject && x.moduleNo === target.moduleNo && x.sort === target.sort);
    return a?.value ?? null;
  }, [answers, target]);

  const meta = useMemo(() => {
    return questions.find((x) => x.subject === target.subject && x.moduleNo === target.moduleNo && x.sort === target.sort);
  }, [questions, target]);

  // ---- 截屏区域选择：overlay 用滚动容器的可视坐标（不受内容 scale 影响）----
  const beginSelect = () => { setSelecting(true); setRect(null); setShotMsg(null); setShotDataUrl(null); };
  const cancelSelect = () => { setSelecting(false); setRect(null); dragStart.current = null; };
  const resetSelect = () => { setRect(null); setShotMsg(null); dragStart.current = null; };

  const overlayPoint = (e: React.PointerEvent) => {
    const b = scrollRef.current!.getBoundingClientRect();
    return { x: e.clientX - b.left, y: e.clientY - b.top };
  };
  const onOverlayDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    const p = overlayPoint(e);
    dragStart.current = p;
    setRect({ x: p.x, y: p.y, w: 0, h: 0 });
  };
  const onOverlayMove = (e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const p = overlayPoint(e);
    setRect({
      x: Math.min(p.x, dragStart.current.x),
      y: Math.min(p.y, dragStart.current.y),
      w: Math.abs(p.x - dragStart.current.x),
      h: Math.abs(p.y - dragStart.current.y),
    });
  };
  const onOverlayUp = () => { dragStart.current = null; };

  const confirmShot = async () => {
    if (!rect || rect.w < 4 || rect.h < 4 || !contentRef.current || !scrollRef.current) return;
    setShotMsg('Capturing…');

    const scroll = scrollRef.current;
    const contentEl = contentRef.current;

    // 选区在“内容未缩放坐标系”下的位置：
    //  (可视坐标 + 滚动偏移 - 内容相对滚动容器偏移) / zoom
    // 注意 offsetLeft/Top 是已缩放布局下相对滚动容器的偏移，除以 zoom 归一到未缩放坐标。
    const cropX = (rect.x + scroll.scrollLeft - contentEl.offsetLeft) / zoom;
    const cropY = (rect.y + scroll.scrollTop - contentEl.offsetTop) / zoom;
    const cropW = rect.w / zoom;
    const cropH = rect.h / zoom;

    // 关键：临时移除 transform，让 html2canvas 面对“未缩放的内容”，
    // 避免它自身解析 transform 与我们的 /zoom 换算叠加导致偏移。
    const prevTransform = contentEl.style.transform;
    const prevWidth = contentEl.style.width;
    contentEl.style.transform = 'none';
    contentEl.style.width = '100%';

    try {
      // 等一帧让布局按未缩放态稳定
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      const RENDER_SCALE = 2;
      // 截取整个未缩放内容，再在返回画布上二次裁剪选区（完全绕开 transform 坐标问题）
      const full = await html2canvas(contentEl, {
        backgroundColor: '#ffffff',
        scale: RENDER_SCALE,
        useCORS: true,
        logging: false,
      });

      const sx = Math.max(0, cropX) * RENDER_SCALE;
      const sy = Math.max(0, cropY) * RENDER_SCALE;
      const sw = Math.min(cropW, full.width / RENDER_SCALE - Math.max(0, cropX)) * RENDER_SCALE;
      const sh = Math.min(cropH, full.height / RENDER_SCALE - Math.max(0, cropY)) * RENDER_SCALE;

      const out = document.createElement('canvas');
      out.width = Math.max(1, Math.round(sw));
      out.height = Math.max(1, Math.round(sh));
      const ctx = out.getContext('2d')!;
      ctx.drawImage(full, sx, sy, sw, sh, 0, 0, out.width, out.height);

      // 直接拿 PNG 的 dataURL（含 base64），留给 Ask AI 用，不走剪贴板
      const dataUrl = out.toDataURL('image/png');
      setShotDataUrl(dataUrl);
      setShotMsg(null);
      setSelecting(false);
      setRect(null);
    } catch (e: any) {
      setShotMsg(e?.message || 'Capture failed');
    } finally {
      // 恢复缩放
      contentEl.style.transform = prevTransform;
      contentEl.style.width = prevWidth;
    }
  };

  return (
    <>
    <DraggableResizablePanel
      title={
        <span className="text-sm font-medium text-gray-700 truncate">
          {cap(target.subject)} · Module {target.moduleNo} · Question {target.sort}
        </span>
      }
      onClose={onClose}
      initialWidth="min(860px, 95vw)"
      initialHeight="min(88vh, 900px)"
      minWidth={360}
      minHeight={360}
      zIndex={50}
      headerExtra={
        <>
          <div className="flex items-center gap-1">
            <button className="w-7 h-7 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
              onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))} title="Zoom out">−</button>
            <span className="text-xs text-gray-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button className="w-7 h-7 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
              onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))} title="Zoom in">+</button>
          </div>
          {selecting ? (
            <>
              <button className="btn btn-primary text-xs py-1 px-3 disabled:opacity-50"
                disabled={!rect || rect.w < 4 || rect.h < 4} onClick={confirmShot}>Confirm</button>
              <button className="btn text-xs py-1 px-3 border border-gray-200" onClick={resetSelect}>Reset</button>
              <button className="btn text-xs py-1 px-3 border border-gray-200" onClick={cancelSelect}>Cancel</button>
            </>
          ) : shotDataUrl ? (
            <>
              <img src={shotDataUrl} alt="snippet" className="h-7 rounded border border-gray-200" />
              <button className="btn btn-primary text-xs py-1 px-3" onClick={() => setAiOpen(true)}>Ask AI</button>
              <button className="btn text-xs py-1 px-3 border border-gray-200" onClick={beginSelect}>Retake</button>
            </>
          ) : (
            <button className="btn btn-primary text-xs py-1 px-3" onClick={beginSelect}>Screenshot</button>
          )}
          {shotMsg && <span className="text-xs text-gray-500">{shotMsg}</span>}
        </>
      }
    >
        {selecting && (
          <div className="px-4 py-1.5 text-xs text-primary-700 bg-primary-50 border-b border-primary-100 shrink-0">
            Drag to select an area, then click Confirm. Adjust zoom first if content is cut off.
          </div>
        )}

        {/* 内容区（滚动容器 = overlay 与截图坐标基准） */}
        <div ref={scrollRef} className="relative flex-1 overflow-auto p-6">
          {loading && <div className="text-gray-400">Loading question…</div>}
          {error && <div className="text-red-500">{error}</div>}
          {!loading && !error && !question && <div className="text-gray-400">Question not found in exam content.</div>}
          {question && (
            <div
              ref={contentRef}
              className="origin-top-left"
              style={{ transform: `scale(${zoom})`, width: `${100 / zoom}%` }}
            >
              <QuestionReview
                question={question}
                userAnswer={userAnswer}
                correct={meta?.correct ?? ''}
                isCorrect={meta?.isCorrect ?? false}
              />
            </div>
          )}

          {/* 选区 overlay：覆盖整个滚动容器，捕获拖拽、画框（不受内容 scale 影响） */}
          {selecting && (
            <div
              className="absolute inset-0 z-10 cursor-crosshair touch-none"
              onPointerDown={onOverlayDown}
              onPointerMove={onOverlayMove}
              onPointerUp={onOverlayUp}
              onPointerCancel={onOverlayUp}
            >
              {rect && (
                <div
                  className="absolute border-2 border-primary-500 bg-primary-500/10 pointer-events-none"
                  style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
                />
              )}
            </div>
          )}
        </div>
    </DraggableResizablePanel>

    {aiOpen && shotDataUrl && (
      <AITutorModal imageDataUrl={shotDataUrl} onClose={() => setAiOpen(false)} />
    )}
    </>
  );
};

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
